import type { AgeTier, CatalogueKey, StarterType } from '../types';

/* Landesverband names (teammeldung.xsd) paired with their three-letter codes
 * (kunstradmeldung.xsd `LV`). Both schemas must agree on the same club. */
export const REGIONAL_FEDERATIONS: ReadonlyArray<readonly [name: string, code: string]> = [
  ['Baden', 'BAD'],
  ['Bayern', 'BAY'],
  ['Berlin', 'BER'],
  ['Brandenburg', 'BRA'],
  ['Bremen', 'BRE'],
  ['Hamburg', 'HAM'],
  ['Hessen', 'HES'],
  ['Mecklenburg-Vorpommern', 'MEV'],
  ['Niedersachsen', 'NDS'],
  ['Nordrhein-Westfalen', 'NRW'],
  ['Rheinland-Pfalz', 'RLP'],
  ['Saarland', 'SAR'],
  ['Sachsen', 'SAC'],
  ['Sachsen-Anhalt', 'SAH'],
  ['Schleswig-Holstein', 'SLH'],
  ['Thüringen', 'THÜ'],
  ['Württemberg', 'WTB'],
  ['Sonstige', 'SON'],
];

export function lvCode(name: string): string {
  return REGIONAL_FEDERATIONS.find((l) => l[0] === name)?.[1] ?? 'SON';
}

/** Reverse of `lvCode()`, for importing kunstradmeldung's 3-letter `LV` back
 *  into the full name `workspace.meta.lv` stores. `undefined` for an
 *  unrecognized code, rather than guessing. */
export function lvName(code: string): string | undefined {
  return REGIONAL_FEDERATIONS.find((l) => l[1] === code)?.[0];
}

/** `Nation` enumeration, common to both schemas, most frequent first. */
export const NATIONS: readonly string[] = [
  'GER', 'AUT', 'SUI', 'FRA', 'NED', 'BEL', 'CZE', 'SVK', 'HUN', 'ITA',
  'ESP', 'POL', 'DEN', 'GBR', 'IRL', 'LUX', 'LIE', 'NOR', 'SWE', 'POR',
  'ROU', 'SLO', 'SRB', 'TUR', 'UKR', 'USA', 'CAN', 'MEX', 'JPN', 'HKG',
  'MAC', 'MAS', 'SIN', 'AUS', 'AFG', 'BUL', 'GRE', 'PER', 'RWA', 'UNK',
];

export interface StarterTypeInfo {
  readonly label: string;
  readonly riders: number;
  /** German discipline prefix, must match the teammeldung.xsd enumeration. */
  readonly prefix: string;
  readonly prefixEn: string;
  readonly catalogue: CatalogueKey;
  /** Whether this type appears in the Teammeldung (1er does not). */
  readonly isTeam: boolean;
}

export const STARTER_TYPES: Readonly<Record<StarterType, StarterTypeInfo>> = {
  single: {
    label: '1er (Einzel)',
    riders: 1,
    prefix: '1er Kunstradsport',
    prefixEn: 'Single Artistic Cycling',
    catalogue: 'single',
    isTeam: false,
  },
  pair: {
    label: '2er (Zweier)',
    riders: 2,
    prefix: '2er Kunstradsport',
    prefixEn: 'Pair Artistic Cycling',
    catalogue: 'pair',
    isTeam: true,
  },
  team4: {
    label: '4er (Vierermannschaft)',
    riders: 4,
    prefix: '4er Kunstradsport',
    prefixEn: 'Artistic Cycling Team 4',
    catalogue: 'team4',
    isTeam: true,
  },
  team6: {
    label: '6er (Sechsermannschaft)',
    riders: 6,
    prefix: '6er Kunstradsport',
    prefixEn: 'Artistic Cycling Team 6',
    catalogue: 'team6',
    isTeam: true,
  },
};

/** Age/sex classes, German name plus the English wording for `DisziplinEnglisch`. */
export const CLASSES: ReadonlyArray<readonly [de: string, en: string]> = [
  ['Elite Frauen', 'Elite Women'],
  ['Elite offen', 'Elite Open'],
  ['Juniorinnen', 'Junior Women'],
  ['Junioren offen', 'Junior Open'],
  ['Schülerinnen', 'Youth Women'],
  ['Schüler offen', 'Youth Open'],
];

/** 6er is only contested in the three open classes. */
export const SIX_CLASSES: readonly string[] = ['Elite offen', 'Junioren offen', 'Schüler offen'];

/** kunstradmeldung.xsd allows at most 30 `Uebung` elements per starter. */
export const MAX_EXERCISES = 30;

export interface ProgramLimits {
  /** Maximum figures in the programme. */
  readonly maxTotal: number;
  /** Pair only: bounds on how many of those figures are ridden on one bicycle
   *  (the rest are on two — both counts are required to be non-zero). */
  readonly onBikeMin?: number;
  readonly onBikeMax?: number;
}

/**
 * Figure-count limits per UCI 8.2.002/8.2.003, keyed by age tier and starter
 * type. Team 4 is not broken down by "on one/two bicycles" — that split only
 * applies to pair — and no rule is given here for Team 6, so it is left out
 * rather than guessed (see the Team 6 catalogue limitation in CLAUDE.md).
 */
export const PROGRAM_LIMITS: Readonly<Record<AgeTier, Partial<Record<StarterType, ProgramLimits>>>> = {
  eliteJunior: {
    single: { maxTotal: 30 },
    pair: { maxTotal: 25, onBikeMin: 8, onBikeMax: 15 },
    team4: { maxTotal: 25 },
  },
  youth: {
    single: { maxTotal: 25 },
    pair: { maxTotal: 20, onBikeMin: 4, onBikeMax: 12 },
    team4: { maxTotal: 25 },
  },
};

/** German label for each "kind of raiser" key the catalogues carry (UCI
 *  8.2.028), for validation messages. Must mirror scripts/build-figures.py
 *  RAISER_LABELS. */
export const RAISER_LABELS: Readonly<Record<string, string>> = {
  'reg-seat': 'Reitsitzsteiger',
  'reg-seat-rev': 'Kehrreitsitzsteiger',
  'lady-seat': 'Damensitzsteiger',
  handlebarseat: 'Lenkersitzsteiger',
  'handlebarseat-rev': 'Kehrlenkersitzsteiger',
  headtube: 'Steuerrohrsteiger',
  'headtube-rev': 'Kehrsteuerrohrsteiger',
  'stand-on-pin': 'Dornstandsteiger',
  'stand-on-pin-rev': 'Kehrdornstandsteiger',
  sidestand: 'Seitenstandsteiger',
  'sidestand-rev': 'Kehrseitenstandsteiger',
  standraiser: 'Standsteiger',
  'standraiser-rev': 'Kehrstandsteiger',
};

/** kunstradmeldung.xsd `Uebung/Deutsch` and `Englisch` maxLength. */
export const MAX_EXERCISE_TEXT = 120;

/** Field length limits taken straight from the schemas. */
export const LIMITS = {
  club: 40,
  federation: 30,
  name: 30,
  uciId: 15,
  countryCode: 3,
  postalCode: 6,
  city: 30,
  street: 30,
  phone: 20,
  fax: 20,
  email: 60,
  competition: 40,
  starterName: 70,
  discipline: 45,
  personalBest: 6,
  scoringReportEmail: 200,
  exerciseNr: 6,
} as const;
